<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
  import qrcodeContainer from '@/assets/images/offline/qrcode_container.png'
  import pageLeftIcon from '@/assets/images/offline/page_left_icon.png'
  import pageRightIcon from '@/assets/images/offline/page_right_icon.png'

  defineOptions({ name: 'App' })

  const CARD_COUNT = 5
  const FEATURE_LIST = [
    'https://static.zaohaowu.com/fe-static/community/pc/offline/feature_illustration/customize_261019.png',
    'https://static.zaohaowu.com/fe-static/community/pc/offline/feature_illustration/free_customize_261019.png',
    'https://static.zaohaowu.com/fe-static/community/pc/offline/feature_illustration/group_buy_261019.png',
    'https://static.zaohaowu.com/fe-static/community/pc/offline/feature_illustration/store_261019.png',
    'https://static.zaohaowu.com/fe-static/community/pc/offline/feature_illustration/buyer_show_261019.png'
  ]
  const viewportRef = ref<HTMLElement | null>(null)
  const trackRef = ref<HTMLElement | null>(null)
  const slideDistance = ref(0)
  const atEnd = ref(false)
  const animating = ref(false)

  let resizeObserver: ResizeObserver | null = null

  /** 末尾偏移 = 轨道总宽 - 当前视口宽，视口变宽时自动缩短 */
  const updateSlideDistance = () => {
    const viewport = viewportRef.value
    const track = trackRef.value
    if (!viewport || !track) return
    slideDistance.value = Math.max(0, track.scrollWidth - viewport.clientWidth)
    if (slideDistance.value === 0) atEnd.value = false
  }

  onMounted(() => {
    updateSlideDistance()
    resizeObserver = new ResizeObserver(updateSlideDistance)
    if (viewportRef.value) resizeObserver.observe(viewportRef.value)
    if (trackRef.value) resizeObserver.observe(trackRef.value)
  })

  onBeforeUnmount(() => {
    resizeObserver?.disconnect()
  })

  const offset = computed(() => (atEnd.value ? -slideDistance.value : 0))
  const showArrow = computed(() => slideDistance.value > 0)
  const arrowIcon = computed(() => (atEnd.value ? pageLeftIcon : pageRightIcon))
  const arrowLabel = computed(() => (atEnd.value ? '回到开始' : '滑到末尾'))
  const iosQrSrc = 'https://static.zaohaowu.com/static/community/pc/app_download_ios.jpg'
  const androidQrSrc =
    'https://static.zaohaowu.com/TestData/tmp/static-resource/c5ce5209-ab61-4247-81ad-c96953bba7cd.jpg'

  const slide = () => {
    if (animating.value) return
    animating.value = true
    atEnd.value = !atEnd.value
  }

  const onTrackTransitionEnd = (event: TransitionEvent) => {
    if (event.propertyName !== 'transform') return
    animating.value = false
  }
</script>

<template>
  <div class="offline-page">
    <div class="offline-stage">
      <section class="offline-lead">
        <p class="offline-title">网站正在维护中，请前往“造好物 App”使用</p>
        <div class="offline-qrcode-box">
          <img class="offline-qrcode" :src="qrcodeContainer" alt="造好物 App 下载" />
          <img class="offline-qr offline-qr--ios" :src="iosQrSrc" alt="iOS 下载二维码" />
          <img
            class="offline-qr offline-qr--android"
            :src="androidQrSrc"
            alt="Android 下载二维码"
          />
        </div>
      </section>

      <section class="offline-gallery" :class="{ 'is-animating': animating }">
        <div ref="viewportRef" class="offline-viewport">
          <div
            ref="trackRef"
            class="offline-track"
            :style="{ transform: `translateX(${offset}px)` }"
            @transitionend="onTrackTransitionEnd"
          >
            <img
              v-for="feature in FEATURE_LIST"
              :key="feature"
              class="offline-card"
              :src="feature"
            />
          </div>
        </div>
        <button
          v-if="showArrow"
          type="button"
          class="offline-arrow"
          :class="atEnd ? 'is-left' : 'is-right'"
          :aria-label="arrowLabel"
          @click="slide"
        >
          <img :src="arrowIcon" alt="" />
        </button>
      </section>
    </div>
  </div>
</template>

<style lang="stylus" scoped>
  .offline-page {
    position: fixed;
    inset: 0;
    z-index: 10001;
    display: flex;
    align-items: center;
    padding-left: calc((100vw - 1440px) / 2);
    overflow: hidden;
    background-image: url('https://static.zaohaowu.com/fe-static/community/pc/offline/bg.png');
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
  }

  .offline-stage {
    display: flex;
    align-items: center;
    min-width: 1440px;
    flex: 1;
    height: 720px;
    flex-shrink: 0;
  }

  .offline-lead {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 26px;
    width: 448px;
    margin-left: 57px;
    margin-right: 86px;
    flex-shrink: 0;
  }

  .offline-title {
    width: 100%;
    margin: 0;
    color: #101010;
    font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
    font-size: 20px;
    font-weight: 500;
    line-height: normal;
    text-align: center;
  }

  .offline-qrcode-box {
    position: relative;
    width: 100%;
  }

  .offline-qrcode {
    display: block;
    width: 100%;
    height: auto;
  }

  .offline-qr {
    position: absolute;
    top: 153.5px;
    width: 108px;
    height: 108px;
    object-fit: contain;
  }

  .offline-qr--ios {
    left: 72.7px;
  }

  .offline-qr--android {
    left: 266.5px;
  }

  .offline-gallery {
    position: relative;
    min-width: 849px;
    flex: 1;
    height: 720px;
    flex-shrink: 0;
  }

  .offline-viewport {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .offline-track {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 100%;
    width: max-content;
    padding: 0 43px;
    transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .offline-card {
    flex-shrink: 0;
    box-sizing: border-box;
    width: 245px;
    height: 530px;
    border: 1px solid #000;
    border-radius: 12px;
  }

  .offline-arrow {
    position: absolute;
    top: 50%;
    z-index: 1;
    width: 52px;
    height: 52px;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    transform: translateY(-50%);
    cursor: pointer;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;

    img {
      display: block;
      width: 52px;
      height: 52px;
    }

    &.is-left {
      left: 0;
    }

    &.is-right {
      right: 24px;
    }
  }

  .offline-gallery:hover .offline-arrow {
    opacity: 1;
    pointer-events: auto;
  }

  .offline-gallery.is-animating .offline-arrow {
    opacity: 0;
    pointer-events: none;
  }
</style>
